import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { onboardingSections } from '../../onboarding/sections';
import { OnboardingAnswers } from '../../types/onboarding';

interface ContinueOnboardingProps {
  setAnswers: React.Dispatch<React.SetStateAction<OnboardingAnswers>>;
}

interface OnboardingSessionData {
  session_id: string;
  seeker_id: string;
  answers: OnboardingAnswers;
  has_basic_info: boolean;
  has_experience: boolean;
  has_medical: boolean;
  current_section_id: string;
  current_question_index: number;
}

export const ContinueOnboarding: React.FC<ContinueOnboardingProps> = ({ setAnswers }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      setError('Invalid continue link - missing session ID');
      setLoading(false);
      return;
    }

    loadOnboardingSession(sessionId);
  }, [searchParams]);

  const loadOnboardingSession = async (sessionId: string) => {
    try {
      const env = (window as any).__ENV__;
      if (!env?.SUPABASE_URL) {
        throw new Error('Missing Supabase configuration');
      }

      // Call the edge function to get session data
      const response = await fetch(
        `${env.SUPABASE_URL}/functions/v1/get-onboarding-session?session_id=${sessionId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'apikey': env.SUPABASE_ANON_KEY,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Onboarding session not found or has expired');
        }
        throw new Error('Failed to load onboarding session');
      }

      const sessionData: OnboardingSessionData = await response.json();

      console.log('Loaded onboarding session:', sessionData);

      // Store the session ID and seeker ID in localStorage
      localStorage.setItem('seeker_onboarding_session', sessionData.session_id);
      localStorage.setItem('ghost_seeker_id', sessionData.seeker_id);

      // Restore answers to state
      setAnswers(sessionData.answers);

      // Also store basic_info_data in localStorage for AccountPasswordSetup
      // This is needed because AccountPasswordSetup reads email from localStorage
      if (sessionData.answers) {
        const basicInfoData = {
          seeker_id: sessionData.seeker_id,
          seeker_email: sessionData.answers.seeker_email,
          seeker_over_18: sessionData.answers.seeker_over_18,
          location_postcode: sessionData.answers.location_postcode,
          seeker_full_name: sessionData.answers.seeker_full_name,
          seeker_phone: sessionData.answers.seeker_phone,
          seeker_gender: sessionData.answers.seeker_gender,
        };
        localStorage.setItem('basic_info_data', JSON.stringify(basicInfoData));
      }

      // Determine where to navigate
      if (sessionData.current_section_id === 'completed') {
        // If they already completed, go to match finding
        navigate('/onboarding/finding-match', { replace: true });
        return;
      }

      // Find the section and first question to resume
      const section = onboardingSections.find(s => s.id === sessionData.current_section_id);

      if (section && section.questions.length > 0) {
        const questionIndex = Math.min(
          sessionData.current_question_index,
          section.questions.length - 1
        );
        const questionId = section.questions[questionIndex].id;

        // Navigate to the specific question
        navigate(`/onboarding/${questionId}`, { replace: true });
      } else {
        // Fallback to first question
        const firstQuestion = onboardingSections[0]?.questions[0]?.id;
        if (firstQuestion) {
          navigate(`/onboarding/${firstQuestion}`, { replace: true });
        } else {
          throw new Error('Unable to determine starting question');
        }
      }
    } catch (err) {
      console.error('Error loading onboarding session:', err);
      setError(err instanceof Error ? err.message : 'Failed to load onboarding session');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
        <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FEF6D6]">
          <header className="flex h-16 items-center border-b border-[#FEF6D6]/25 bg-[#2C2731] px-4">
            <div className="w-10" aria-hidden />
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>
          <main className="flex flex-1 min-h-0 flex-col items-center justify-center text-center px-6">
            <div className="animate-fade-in flex flex-col items-center gap-4">
              <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#304031]" />
              <h1 className="font-recoleta text-[32px] font-normal leading-[1.1] text-[#304031]">
                Loading your onboarding
              </h1>
              <p className="max-w-sm text-[16px] leading-relaxed text-[#214D3C]">
                We're retrieving your saved answers...
              </p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
        <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FEF6D6]">
          <header className="flex h-16 items-center border-b border-[#FEF6D6]/25 bg-[#2C2731] px-4">
            <div className="w-10" aria-hidden />
            <div className="flex flex-1 justify-center">
              <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
            </div>
            <div className="w-10" aria-hidden />
          </header>
          <main className="flex flex-1 min-h-0 flex-col items-center justify-center text-center px-6">
            <div className="animate-fade-in flex flex-col items-center gap-4">
              <div className="text-6xl">⚠️</div>
              <h1 className="font-recoleta text-[32px] font-normal leading-[1.1] text-[#304031]">
                Unable to Continue
              </h1>
              <p className="max-w-sm text-[16px] leading-relaxed text-[#214D3C]">
                {error}
              </p>
              <button
                type="button"
                onClick={() => navigate('/', { replace: true })}
                className="mt-4 inline-flex items-center justify-center rounded-full border border-[#304031] bg-[#FEF6D6] px-6 py-3 text-base font-semibold text-[#304031] transition hover:bg-[#304031] hover:text-[#FEF6D6]"
              >
                Start New Onboarding
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // This shouldn't be reached, but just in case
  return null;
};
